/**
 * Computes which coach-profile sections are visible and assigns sequential
 * 1-based numbers in declaration order. Hidden sections are skipped so the
 * numbering on the rendered page never has visible gaps.
 *
 * Single source of truth shared by the main profile route
 * (`/$username`) and the discovery profile route
 * (`/discovery/$username`) so they stay in lockstep.
 *
 * Aliases: section number, profile numbering, renumber sections
 */

type SectionKey =
  | "about"
  | "helpsWith"
  | "outcomes"
  | "approach"
  | "coaching"
  | "credentials"
  | "testimonials"
  | "also";

export type CoachSectionNumbers = Partial<Record<SectionKey, number>>;

type Input = {
  profile: {
    description: string | null;
    challengesDescription: string | null;
    outcomesDescription: string | null;
    whyContent: string | null;
    leadMagnetTitle: string | null;
    leadMagnetUrl: string | null;
  };
  mainOffer: { headline: string | null } | null | undefined;
  results: readonly unknown[] | null | undefined;
  testimonials: readonly unknown[] | null | undefined;
  /** Featured testimonial extracted from `testimonials` — when present, one
   * testimonial is lifted into the pull-quote band, so the section requires
   * more than one to render. */
  featuredTestimonial: unknown | null;
  cihChallenges: readonly { slug: string }[];
  cihArticles: readonly { slug: string }[];
  cihFrameworks: readonly { slug: string }[];
  credentialCount: number;
  /** The coach's Wheel of Life page is live: "Also from me" shows its block. */
  hasWheelPage?: boolean;
};

export function computeCoachSectionNumbers(input: Input): CoachSectionNumbers {
  const { profile } = input;

  const hasLeadMagnet = Boolean(
    profile.leadMagnetTitle?.trim() && profile.leadMagnetUrl?.trim(),
  );
  const hasAlsoCards =
    input.cihArticles.length > 0 || input.cihFrameworks.length > 0;

  const visibility: Record<SectionKey, boolean> = {
    about: Boolean(profile.description?.trim()),
    helpsWith:
      Boolean(profile.challengesDescription?.trim()) ||
      input.cihChallenges.length > 0,
    outcomes:
      Boolean(profile.outcomesDescription?.trim()) ||
      (input.results?.length ?? 0) > 0,
    approach: Boolean(profile.whyContent?.trim()),
    coaching: Boolean(input.mainOffer?.headline?.trim()),
    credentials: input.credentialCount > 0,
    testimonials:
      (input.testimonials?.length ?? 0) > (input.featuredTestimonial ? 1 : 0),
    also: hasLeadMagnet || hasAlsoCards || input.hasWheelPage === true,
  };

  const order: readonly SectionKey[] = [
    "about",
    "helpsWith",
    "outcomes",
    "approach",
    "coaching",
    "credentials",
    "testimonials",
    "also",
  ];

  const result: CoachSectionNumbers = {};
  let n = 1;
  for (const key of order) {
    if (visibility[key]) {
      result[key] = n;
      n += 1;
    }
  }
  return result;
}
